import { Suspense, useEffect } from "react";
import { useBarcode } from "react-barcodes";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Redirect, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Loader, ScrollableContainer, StyledPageWrapper, Title } from "@monorepo/components";
import { ENVIRONMENT } from "@monorepo/constants";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { convertNumberToCurrency, isResponseErrorArray } from "@monorepo/helpers";
import { usePayCashMockMutation } from "@monorepo/services/api/carts";
import {
  orderApi,
  useCancelOrderMutation,
  useGetActiveOrderQuery,
  useGetOrderByIdQuery,
  useGetOrderInvoiceQuery,
} from "@monorepo/services/api/order";
import theme from "@monorepo/styles/theme";
import { EnvCodeEnum, OrderStatusIdEnum } from "@monorepo/types";
import CancelIcon from "@mui/icons-material/Cancel";
import IosShareIcon from "@mui/icons-material/IosShare";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Button, styled, Typography } from "@mui/material";
import { reverse } from "named-urls";

const INVOICE_FILE_NAME = "invoice.pdf";

interface ICashPaymentProps {
  orderId: string;
}

interface IBarCodeProps {
  code: string;
}

const StyledLink = styled("a")(() => ({
  color: theme.palette.text.secondary,
  gridColumn: "1/3",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  columnGap: theme.spacing(1),
}));

const Barcode: React.FC<IBarCodeProps> = ({ code }) => {
  const { inputRef } = useBarcode({
    value: code,
    options: {
      displayValue: false,
      background: "transparent",
    },
  });
  return <svg ref={inputRef}></svg>;
};

const CashPayment: React.FC<ICashPaymentProps> = ({ orderId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const { data: order, isFetching, isError } = useGetOrderByIdQuery(orderId);
  const { data: activeOrder, isSuccess } = useGetActiveOrderQuery();
  const [cancelOrder, { isLoading: cancelOrderIsLoading }] = useCancelOrderMutation();
  const [mockPay, { isSuccess: paymentSuccess }] = usePayCashMockMutation();
  const { data: invoice, isFetching: invoiceIsFetching, refetch } = useGetOrderInvoiceQuery();

  useEffect(() => {
    refetch();
  }, [activeOrder, refetch]);

  useEffect(() => {
    if (paymentSuccess && activeOrder && activeOrder.id) {
      history.push(
        reverse(routeConfig.dashboard.payments.orderProcessing.self, { orderId: activeOrder?.id }) + "?success=1"
      );
    }
  }, [activeOrder, history, paymentSuccess]);

  const pdf = new File([invoice!], INVOICE_FILE_NAME, { type: "application/pdf" });
  const files: Array<File> = [pdf];
  const canShare = navigator && navigator.canShare && navigator.canShare({ files });
  const showMockPay =
    ENVIRONMENT === EnvCodeEnum.LOCAL ||
    ENVIRONMENT === EnvCodeEnum.STAGING ||
    ENVIRONMENT === EnvCodeEnum.TEST ||
    ENVIRONMENT === EnvCodeEnum.SANDBOX;

  const handleCancelOrder = async (id: string) => {
    cancelOrder(id)
      .unwrap()
      .then((res) => {
        if (res.success) {
          dispatch(orderApi.util.resetApiState());
          history.replace(routeConfig.dashboard.payments.self);
          if (activeOrder && activeOrder.id) {
            history.push(
              reverse(routeConfig.dashboard.payments.orderProcessing.self, {
                orderId: activeOrder.id,
              }) + "?cancel=1"
            );
          }
        }
      })
      .catch((err) => {
        if (isResponseErrorArray(err)) {
          err.forEach((error) => {
            toast.error(error.title);
          });
        }
      });
  };

  if (
    isError ||
    order?.status_id === OrderStatusIdEnum.CANCELED_MANUALLY ||
    order?.status_id === OrderStatusIdEnum.CANCELED
  ) {
    toast.error(t("payments.order_is_not_found"));
    return <Redirect to={routeConfig.dashboard.payments.self} />;
  }

  if (isFetching || invoiceIsFetching) {
    return (
      <StyledPageWrapper>
        <Loader />
      </StyledPageWrapper>
    );
  }

  if (!isFetching && !invoiceIsFetching && isSuccess && order?.barcode_image) {
    return (
      <Suspense fallback={<Loader />}>
        <StyledPageWrapper>
          <Box sx={{ display: "grid", gridRowGap: theme.spacing(1), textAlign: "center" }}>
            <Title>{t("payments.payment_method")}</Title>
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
              {t("payments.pay_in_cash")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridRowGap: theme.spacing(1),
            }}
          >
            <Typography component="span" variant="body1" textAlign="center" sx={{ color: theme.palette.text.disabled }}>
              {t("payments.pay_in_total")}
            </Typography>
            {activeOrder?.total && (
              <Typography variant="h4" component="span" textAlign="center" fontWeight="bold">
                {convertNumberToCurrency(activeOrder.total)}
              </Typography>
            )}
          </Box>
          <ScrollableContainer>
            <Box
              sx={{
                display: "grid",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                gridRowGap: { xs: theme.spacing(2), lg: theme.spacing(3) },
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  justifyContent: "center",
                }}
              >
                <Barcode code={order.barcode_image} />
              </Box>
              <Box
                sx={{
                  display: "grid",
                  alignItems: "center",
                  gridTemplateColumns: "1fr auto",
                  gridRowGap: theme.spacing(2),
                  textAlign: "left",
                }}
              >
                <Box>
                  <Typography fontWeight="bold">{order.barcode}</Typography>
                  <Typography sx={{ color: theme.palette.text.disabled }}>{t("payments.unique_payment_id")}</Typography>
                </Box>
                {canShare && (
                  <Button
                    size="small"
                    sx={{ minWidth: "unset", px: theme.spacing(3) }}
                    onClick={async () => {
                      await window.navigator.share({
                        files,
                        text: `${t("payments.pay_in_total")} ${convertNumberToCurrency(activeOrder?.total!)}\n${t(
                          "payments.unique_payment_id"
                        )} ${order.barcode}`,
                      });
                    }}
                  >
                    <IosShareIcon sx={{ display: "inherit" }} fontSize="small" />
                  </Button>
                )}
                {invoice && !invoiceIsFetching && (
                  <StyledLink href={window.URL.createObjectURL(new Blob([invoice]))} download={INVOICE_FILE_NAME}>
                    <PictureAsPdfIcon color="inherit" />
                    <strong>{t("payments.download_invoice_pdf")}</strong>
                  </StyledLink>
                )}
              </Box>
            </Box>
          </ScrollableContainer>
          <LoadingButton
            variant="contained"
            color="neutral"
            disableElevation
            loading={cancelOrderIsLoading}
            disabled={cancelOrderIsLoading}
            startIcon={<CancelIcon />}
            onClick={async () => {
              if (order.id) {
                await handleCancelOrder(order.id);
              }
            }}
          >
            {t("payments.decline_payment")}
          </LoadingButton>
          {showMockPay && (
            <Button
              variant="contained"
              color="neutral"
              disableElevation
              startIcon={<CancelIcon />}
              onClick={async () => {
                if (order.id) {
                  await mockPay(order.id);
                }
              }}
            >
              {t("payments.mock_pay")}
            </Button>
          )}
        </StyledPageWrapper>
      </Suspense>
    );
  }

  return null;
};

export default CashPayment;
